import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, PhotoCamera } from '@mui/icons-material';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import liveScreenSocket, { LiveActivityEventPayload } from '../../services/liveScreenSocket';
import {
  actionDescriptionFromEvent,
  dedupeLiveActivityEvents,
  describeActivityAction,
  friendlyAppName,
} from '../../utils/actionDescription';

const DISPLAY_TZ = 'America/Chicago';
const MAX_EVENTS = 100;

export interface LiveActivityFeedProps {
  deviceId: string;
  enabled?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  canViewSnapshots?: boolean;
  onViewSnapshot?: (snapshotId: string, capturedAt?: string | null) => void;
}

function formatEventTime(iso?: string | null): string {
  if (!iso) return '—';
  return moment(iso).tz(DISPLAY_TZ).format('h:mm:ss A');
}

function enrichLiveEvent(event: LiveActivityEventPayload): LiveActivityEventPayload {
  const { label, context } = actionDescriptionFromEvent({
    event_type: event.event_type,
    label: event.description,
    context: event.context,
    description: event.description,
    application_name: event.application_name,
    domain: event.domain,
  });
  return {
    ...event,
    application_name: friendlyAppName(event.application_name) || event.application_name,
    description: label,
    context,
  };
}

function mapTimelineEventToLive(event: any): LiveActivityEventPayload | null {
  if (!event) return null;
  const eventId = event.event_id || event.id;
  if (!eventId) return null;
  const detail = event.detail || {};
  const { label, context } = actionDescriptionFromEvent({
    event_type: event.event_type || event.type,
    label: event.label || event.description || event.summary,
    context: event.context || event.detail_text,
    description: event.description,
    application_name: event.application_name || event.application || detail.application_name || detail.process_name,
    process_name: event.process_name || detail.process_name,
    window_title: event.window_title || detail.window_title,
    domain: event.domain || detail.domain,
    page_title: event.page_title || detail.page_title,
    duration_seconds: event.duration_seconds ?? detail.duration_seconds ?? null,
    detail,
  });
  return {
    event_id: String(eventId),
    device_uid: event.device_uid || event.deviceId || null,
    device_id: event.device_id || event.device_internal_id || null,
    employee_id: event.employee_id || null,
    employee_name: event.employee_name || null,
    tenant_id: event.tenant_id,
    event_type: event.event_type || event.type,
    description: label,
    context: context || event.context || null,
    application_name: friendlyAppName(
      event.application_name || event.application || event.process_name || detail.application_name || detail.process_name,
    ),
    domain: event.domain || null,
    monitor_id: event.monitor_id || null,
    snapshot_id: event.snapshot_id || event.detail?.snapshot_id || null,
    campaign_id: event.campaign_id || null,
    campaign: event.campaign || event.campaign_name || null,
    started_at: event.started_at || event.timestamp || event.start_time || null,
    source: event.source || null,
  };
}

const LiveActivityFeed: React.FC<LiveActivityFeedProps> = ({
  deviceId,
  enabled = true,
  collapsed = false,
  onToggleCollapse,
  canViewSnapshots = false,
  onViewSnapshot,
}) => {
  const [events, setEvents] = useState<LiveActivityEventPayload[]>([]);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subscribedRef = useRef(false);
  const deviceIdRef = useRef(deviceId);

  useEffect(() => {
    deviceIdRef.current = deviceId;
    if (!enabled || !deviceId) {
      setConnecting(false);
      return undefined;
    }

    let cancelled = false;

    const mergeEvents = (incoming: LiveActivityEventPayload[]) => {
      setEvents((prev) => {
        const byId = new Map<string, LiveActivityEventPayload>();
        [...incoming.map(enrichLiveEvent), ...prev].forEach((ev) => {
          const key = `${ev.event_id}-${ev.started_at || ''}`;
          if (!byId.has(key)) byId.set(key, ev);
        });
        return dedupeLiveActivityEvents<LiveActivityEventPayload>(Array.from(byId.values()))
          .sort((a, b) => String(b.started_at || '').localeCompare(String(a.started_at || '')))
          .slice(0, MAX_EVENTS);
      });
    };

    const offActivity = liveScreenSocket.onLiveActivity((event) => {
      const eventDevice = event.device_uid || event.device_id;
      if (eventDevice && eventDevice !== deviceId && eventDevice !== deviceIdRef.current) return;
      mergeEvents([event]);
    });

    const run = async () => {
      setConnecting(true);
      setError(null);
      setEvents([]);
      try {
        // Seed from persisted activity so the panel is not blank until the next switch.
        try {
          const recent = await apiClient.monitoring.getActivity({
            device_id: deviceId,
            page_size: 40,
            page: 1,
            sort_by: 'timestamp',
            sort_dir: 'DESC',
            feed: 'computer',
            date: moment().tz(DISPLAY_TZ).format('YYYY-MM-DD'),
          });
          if (!cancelled) {
            const rows = recent?.data?.data?.events || [];
            const mapped = rows
              .map(mapTimelineEventToLive)
              .filter(Boolean) as LiveActivityEventPayload[];
            if (mapped.length) mergeEvents(mapped);
          }
        } catch {
          // Live socket subscription still proceeds without history.
        }

        await liveScreenSocket.connect();
        if (cancelled) return;
        await liveScreenSocket.subscribeActivity(deviceId);
        if (cancelled) {
          await liveScreenSocket.unsubscribeActivity(deviceId).catch(() => undefined);
          return;
        }
        subscribedRef.current = true;
        setConnecting(false);
      } catch (err: any) {
        if (!cancelled) {
          subscribedRef.current = false;
          setConnecting(false);
          setError(err?.message || 'Unable to subscribe to live activity');
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      offActivity();
      if (subscribedRef.current) {
        subscribedRef.current = false;
        void liveScreenSocket.unsubscribeActivity(deviceIdRef.current).catch(() => undefined);
      }
    };
  }, [enabled, deviceId]);

  const emptyMessage = useMemo(() => {
    if (error) return error;
    if (connecting) return 'Connecting to live activity feed…';
    return 'No activity yet. App switches and actions will appear here as they happen.';
  }, [connecting, error]);

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: collapsed ? 56 : 320,
        overflow: 'hidden',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        px={1.5}
        py={1}
        borderBottom={collapsed ? 0 : 1}
        borderColor="divider"
      >
        <Box minWidth={0}>
          <Typography variant="subtitle2" fontWeight={700}>Live Activity</Typography>
          {!collapsed && (
            <Typography variant="caption" color="text.secondary" display="block">
              What this computer is doing — apps, tabs, and actions as they happen.
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          {!collapsed && (
            <Chip
              size="small"
              color={connecting ? 'info' : error ? 'warning' : 'success'}
              label={connecting ? 'CONNECTING' : error ? 'UNAVAILABLE' : 'LIVE FEED'}
            />
          )}
          {onToggleCollapse && (
            <Tooltip title={collapsed ? 'Show activity panel' : 'Hide activity panel'}>
              <IconButton size="small" onClick={onToggleCollapse} aria-label="Toggle activity panel">
                {collapsed ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Collapse in={!collapsed} sx={{ flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: { xs: 280, md: 'none' }, p: 1 }}>
          {events.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 0.5 }}>
              {emptyMessage}
            </Typography>
          ) : (
            events.map((event) => {
              const snapshotId = event.snapshot_id;
              return (
                <Box
                  key={`${event.event_id}-${event.started_at}`}
                  sx={{
                    py: 1,
                    px: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <Box display="flex" justifyContent="space-between" gap={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {formatEventTime(event.started_at)}
                    </Typography>
                    {canViewSnapshots && snapshotId && onViewSnapshot && (
                      <Tooltip title="View evidence snapshot">
                        <IconButton
                          size="small"
                          aria-label="View snapshot"
                          onClick={() => onViewSnapshot(snapshotId, event.started_at || null)}
                        >
                          <PhotoCamera fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {event.description
                      || describeActivityAction({
                        eventType: event.event_type,
                        applicationName: event.application_name,
                      })
                      || 'Activity'}
                  </Typography>
                  {event.context && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {event.context}
                    </Typography>
                  )}
                </Box>
              );
            })
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default LiveActivityFeed;
